import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "../components/ui/button";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import Testimonials from "../components/ui/Testimonials";
import ParallaxImage from "../components/ui/ParallaxImage";
import { ArrowRight, ArrowUpRight, ChevronRight, ChevronLeft } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { API } from "../apiConfig";

const LIFESTYLE_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/0cm4uivt_6txuzv8pt.jpeg",
  hotel: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/hftgi43w_5lihziuyg.jpeg",
  gallery: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/wzkhpmgr_4urfvhgzmuj8s.jpeg",
  boardroom: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/lflehik0_4urfvhgzmuj8s.jpeg",
};

const LOOKBOOK_IMAGES = [
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/hftgi43w_5lihziuyg.jpeg", caption: "Private Residence — Emu artwork in contemporary living space" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/0cm4uivt_6txuzv8pt.jpeg", caption: "Master Bedroom — Wallaby pyrography as a statement headpiece" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/dg6t0xq0_1je24xjmu.jpeg", caption: "Luxury Suite — Gadigal Culture artwork above the bed" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/ig4op7k0_h9yevw999kbph.jpeg", caption: "Gallery Hallway — Sea creatures pyrography in a curated setting" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/wzkhpmgr_4urfvhgzmuj8s.jpeg", caption: "Gallery Wall — The White Wallaby on display" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/lflehik0_4urfvhgzmuj8s.jpeg", caption: "Contemporary Interior — Emu pyrography as a focal point" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/rehsq7u7_1je24xjmu.jpeg", caption: "Designer Suite — Ancestral Spirits original pyrography" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/c3079988_hpgui9asyxflr.jpeg", caption: "Family Room — Ancestral pyrography bringing culture into the home" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/c1etqfbf_jgezsafxyx55r.jpeg", caption: "Living Room — Ancestral Spirits pyrography in a bright interior" },
  { src: "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/7zcin9ey_8m10pyfqa.jpeg", caption: "Lounge — Wallaby art above the fireplace" },
];

const CONTENTS = [
  { no: "01", title: "The Collection", desc: "Original pyrography, limited editions & cultural stories", href: "/the-collection" },
  { no: "02", title: "The Artist", desc: "Colin Isaacs — Gadigal & Bidjigal Elder, The White Wallaby", href: "/artist" },
  { no: "03", title: "Commissions", desc: "Bespoke works created for considered spaces", href: "/commissions" },
  { no: "04", title: "Silent Auctions", desc: "Rare works offered to registered collectors", href: "/auctions" },
  { no: "05", title: "Trade Enquiries", desc: "Galleries, designers & corporate placements", href: "/quote-request" },
];

const pad = (n) => String(n).padStart(2, "0");

const LookbookSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center", skipSnaps: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  return (
    <section className="py-28 bg-[#141210]" data-testid="insitu-section">
      <div className="max-w-7xl mx-auto px-6 md:px-16 mb-14">
        <div className="flex items-center justify-between border-b border-white/15 pb-4 mb-10">
          <p className="folio text-white/50">Chapter III — In Situ</p>
          <p className="plate-no text-white/30 hidden md:block">Where Arawol Lives</p>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-light text-white leading-none"
          >
            Art That <em>Transforms</em> Spaces
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="text-white/50 max-w-md text-sm leading-relaxed"
          >
            From private residences to corporate headquarters, Arawol pieces create a
            statement that sparks conversation and cultural connection.
          </motion.p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative"
      >
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {LOOKBOOK_IMAGES.map((img, i) => (
              <div key={i} className="flex-[0_0_90%] md:flex-[0_0_65%] lg:flex-[0_0_55%] min-w-0 px-3">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                    loading={i < 2 ? "eager" : "lazy"}
                    data-testid={`lookbook-slide-${i}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="flex items-center justify-between mt-8">
            <p className="text-sm text-white/60 italic max-w-md font-accent text-lg">
              {LOOKBOOK_IMAGES[selectedIndex]?.caption}
            </p>
            <div className="flex items-center gap-6">
              <span className="plate-no text-white/40 tabular-nums">
                {pad(selectedIndex + 1)} / {pad(LOOKBOOK_IMAGES.length)}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={scrollPrev}
                  className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
                  data-testid="lookbook-prev"
                >
                  <ChevronLeft className="h-5 w-5 text-white" strokeWidth={1.5} />
                </button>
                <button
                  onClick={scrollNext}
                  className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
                  data-testid="lookbook-next"
                >
                  <ChevronRight className="h-5 w-5 text-white" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 w-full h-px bg-white/10 overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              animate={{ width: `${((selectedIndex + 1) / LOOKBOOK_IMAGES.length) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>

          <div className="mt-10">
            <Link to="/quote-request" data-testid="lookbook-trade-link">
              <Button variant="outline" className="rounded-full px-8 h-12 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
                Enquire About Corporate & Trade
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

const HomePage = () => {
  const [artist, setArtist] = useState(null);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [cmsContent, setCmsContent] = useState({});

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.25]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const [artistsRes, cmsRes, prodRes, artRes] = await Promise.all([
          axios.get(`${API}/artists`),
          axios.get(`${API}/cms/homepage`).catch(() => ({ data: [] })),
          axios.get(`${API}/products`).catch(() => ({ data: [] })),
          axios.get(`${API}/articles?limit=3`).catch(() => ({ data: [] })),
        ]);
        if (artistsRes.data.length > 0) setArtist(artistsRes.data[0]);
        const cmsObj = {};
        cmsRes.data.forEach((item) => { cmsObj[item.section] = item.content; });
        setCmsContent(cmsObj);
        let prods = prodRes.data;
        if (prods && !Array.isArray(prods)) prods = prods.products || [];
        prods = (prods || []).filter((p) => p.is_public !== false);
        const featured = [...prods.filter((p) => p.is_featured), ...prods.filter((p) => !p.is_featured)];
        setProducts(featured.slice(0, 7));
        setArticles(Array.isArray(artRes.data) ? artRes.data : []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const cms = (key, fallback) => cmsContent[key] || fallback;

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Navigation />

      {/* ═══ COVER — full-bleed masthead ═══ */}
      <section ref={heroRef} className="relative h-[100svh] overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <img
            src={LIFESTYLE_IMAGES.hero}
            alt="Colin Isaacs Aboriginal artwork in luxury interior"
            className="w-full h-[115%] object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/40" />

        {/* Masthead folio row */}
        <div className="absolute top-24 md:top-28 inset-x-0 px-6 md:px-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="flex justify-between items-center border-b border-white/25 pb-4 text-white/70"
          >
            <span className="folio">Vol. I — The White Wallaby Collection</span>
            <span className="folio hidden md:block">Gadigal Country, Australia</span>
            <span className="folio hidden sm:block">Est. 65,000 Years</span>
          </motion.div>
        </div>

        {/* Cover headline */}
        <div className="absolute bottom-14 md:bottom-20 inset-x-0 px-6 md:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
            <div className="max-w-3xl">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="folio text-white/60 mb-6"
              >
                Authentic Aboriginal Fine Art — By Elder Colin Isaacs
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.9 }}
                className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light text-white leading-[0.95] tracking-tight"
                data-testid="hero-title"
              >
                {cms("hero_title", "Collected.")}
                <br />
                <em className="text-white/85">{cms("hero_subtitle", "Not just displayed.")}</em>
              </motion.h1>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="flex flex-col items-start gap-5 shrink-0 pb-2"
            >
              <Link to="/the-collection" data-testid="hero-explore-btn">
                <Button size="lg" className="rounded-full px-10 h-14 text-base">
                  View the Collection
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link
                to="/artist"
                className="underline-link text-white text-sm tracking-widest uppercase"
                data-testid="hero-artist-btn"
              >
                Meet the Artist
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ IN THIS ISSUE — table of contents ═══ */}
      <section className="py-24 md:py-32 px-6 md:px-16" data-testid="contents-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <p className="folio text-primary">In This Issue</p>
            <p className="plate-no text-muted-foreground hidden md:block">Art By Arawol — Contents</p>
          </div>
          <div>
            {CONTENTS.map((item, i) => (
              <motion.div
                key={item.no}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <Link
                  to={item.href}
                  className="group grid grid-cols-12 items-baseline gap-4 py-7 border-t border-foreground/15 hover:bg-muted/40 transition-colors px-2 -mx-2"
                  data-testid={`contents-link-${item.no}`}
                >
                  <span className="plate-no text-primary col-span-2 md:col-span-1">{item.no}</span>
                  <h3 className="col-span-9 md:col-span-4 text-2xl md:text-4xl font-light group-hover:text-primary transition-colors duration-300">
                    <em>{item.title}</em>
                  </h3>
                  <p className="hidden md:block col-span-6 text-sm text-muted-foreground">{item.desc}</p>
                  <ArrowUpRight
                    className="col-span-1 h-5 w-5 justify-self-end text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={1.5}
                  />
                </Link>
              </motion.div>
            ))}
            <div className="border-t border-foreground/15" />
          </div>
        </div>
      </section>

      {/* ═══ CHAPTER I — SELECTED WORKS (horizontal rail) ═══ */}
      {products.length > 0 && (
        <section className="py-8 md:py-16" data-testid="selected-works-section">
          <div className="max-w-7xl mx-auto px-6 md:px-16 mb-12">
            <div className="flex items-center justify-between border-b border-foreground/15 pb-4 mb-10">
              <p className="folio text-primary">Chapter I — The Gallery</p>
              <p className="plate-no text-muted-foreground hidden md:block">Selected Works</p>
            </div>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <h2 className="text-4xl md:text-6xl font-light leading-none">
                Selected <em>Works</em>
              </h2>
              <Link
                to="/the-collection"
                className="underline-link text-sm tracking-widest uppercase text-foreground"
                data-testid="selected-works-view-all"
              >
                The Full Collection
              </Link>
            </div>
          </div>

          <div
            className="mag-rail flex gap-8 md:gap-12 overflow-x-auto pb-10 px-6 md:px-16 snap-x snap-mandatory"
            data-lenis-prevent
            data-testid="works-rail"
          >
            {products.map((product, i) => (
              <Link
                key={product.product_id}
                to={`/products/${product.product_id}`}
                className="snap-start shrink-0 w-[78vw] sm:w-[46vw] lg:w-[30vw] group"
                data-testid={`rail-work-${product.product_id}`}
              >
                <div className={`${i % 2 === 0 ? "aspect-[3/4]" : "aspect-[4/5] mt-8 md:mt-14"} overflow-hidden mb-5 image-protection-wrapper`}>
                  <img
                    src={product.images?.[0] || ""}
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04] artwork-image"
                    loading={i < 2 ? "eager" : "lazy"}
                    draggable="false"
                  />
                </div>
                <p className="plate-no text-muted-foreground mb-2">Plate N°{pad(i + 1)}</p>
                <h3 className="text-xl md:text-2xl font-light group-hover:text-primary transition-colors duration-300">
                  <em>{product.title}</em>
                </h3>
                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mt-2">
                  {product.medium || product.category?.replace("_", " ")}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ═══ FULL-BLEED INTERLUDE — parallax spread ═══ */}
      <ParallaxImage
        src={LIFESTYLE_IMAGES.gallery}
        alt="The White Wallaby artwork on a gallery wall"
        className="h-[70vh] md:h-[88vh]"
        data-testid="interlude-spread"
      >
        <div className="absolute inset-0 bg-black/40 flex items-end">
          <div className="w-full px-6 md:px-16 pb-14 md:pb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="max-w-4xl"
            >
              <p className="folio text-white/50 mb-6">From the Artist</p>
              <p className="font-accent italic text-3xl md:text-5xl text-white leading-tight">
                “Art is my way of speaking to the world. Every mark I make carries a story.”
              </p>
              <p className="folio text-white/60 mt-6">— Colin Isaacs, The White Wallaby</p>
            </motion.div>
          </div>
        </div>
      </ParallaxImage>

      {/* ═══ CHAPTER II — THE ARTIST ═══ */}
      {artist && (
        <section className="py-24 md:py-32 px-6 md:px-16" data-testid="artist-preview-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between border-b border-foreground/15 pb-4 mb-16">
              <p className="folio text-primary">Chapter II — The Artist</p>
              <p className="plate-no text-muted-foreground hidden md:block">Gadigal & Bidjigal Elder</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-7"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={artist.image_url || "https://images.unsplash.com/photo-1525599428495-0441bd5c67de"}
                    alt={artist.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="plate-no text-muted-foreground mt-4">Portrait — The White Wallaby</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="lg:col-span-5 lg:pt-16"
              >
                <h2 className="text-5xl md:text-6xl font-light leading-none mb-8">
                  <em>{artist.name}</em>
                </h2>
                <p className="font-accent text-2xl italic text-primary mb-8">
                  "Art from Country. For Country."
                </p>
                <p className="drop-cap text-muted-foreground leading-loose mb-10">
                  {artist.biography}
                </p>
                <Link
                  to="/artist"
                  className="underline-link text-sm tracking-widest uppercase text-foreground"
                  data-testid="artist-read-more-btn"
                >
                  Read His Story
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ ASSURANCE STRIP ═══ */}
      <section className="border-y border-foreground/15 py-8 px-6 md:px-16" data-testid="assurance-strip">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center md:justify-between gap-x-10 gap-y-3">
          {["Certificate of Authenticity", "Strictly Limited Editions", "White-Glove Worldwide Delivery", "Provenance Documented"].map((t, i) => (
            <span key={i} className="folio text-muted-foreground flex items-center gap-10">
              {t}
              {i < 3 && <span className="hidden md:block w-1 h-1 rounded-full bg-primary" />}
            </span>
          ))}
        </div>
      </section>

      {/* ═══ CHAPTER III — IN SITU ═══ */}
      <LookbookSection />

      {/* ═══ CHAPTER IV — JOURNAL ═══ */}
      {articles.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-16" data-testid="journal-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between border-b border-foreground/15 pb-4 mb-14">
              <p className="folio text-primary">Chapter IV — The Journal</p>
              <p className="plate-no text-muted-foreground hidden md:block">Stories & Provenance</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-4">
                <h2 className="text-4xl md:text-5xl font-light leading-tight mb-6">
                  Our <em>Stories</em>
                </h2>
                <p className="text-muted-foreground leading-relaxed max-w-sm mb-8">
                  Essays on Aboriginal art, collecting with integrity, and the cultural
                  significance behind every work in the collection.
                </p>
                <Link
                  to="/the-collection"
                  className="underline-link text-sm tracking-widest uppercase text-foreground"
                  data-testid="journal-view-all"
                >
                  All Stories
                </Link>
              </div>
              <div className="lg:col-span-8">
                {articles.map((article, i) => (
                  <motion.div
                    key={article.slug || i}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                  >
                    <Link
                      to={`/articles/${article.slug}`}
                      className="group flex items-start gap-6 py-8 border-t border-foreground/15"
                      data-testid={`journal-article-${i}`}
                    >
                      <span className="plate-no text-primary pt-2 shrink-0">{pad(i + 1)}</span>
                      {article.featured_image && (
                        <div className="hidden sm:block w-28 h-20 overflow-hidden shrink-0">
                          <img
                            src={article.featured_image}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl md:text-2xl font-light group-hover:text-primary transition-colors duration-300 leading-snug">
                          <em>{article.title}</em>
                        </h3>
                        {article.excerpt && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{article.excerpt}</p>
                        )}
                      </div>
                      <ArrowUpRight
                        className="h-5 w-5 mt-2 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 shrink-0"
                        strokeWidth={1.5}
                      />
                    </Link>
                  </motion.div>
                ))}
                <div className="border-t border-foreground/15" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ THE DREAMTIME — editorial essay ═══ */}
      <section className="py-24 px-6 md:px-16 bg-muted/40 border-t border-foreground/10" data-testid="dreamtime-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div>
            <p className="folio text-primary mb-5">The Dreamtime</p>
            <h2 className="text-4xl md:text-5xl font-light leading-tight">
              Ancient Stories,
              <br />
              <em>Living Art</em>
            </h2>
          </div>
          <div className="lg:col-span-2 space-y-6">
            <p className="drop-cap text-lg text-muted-foreground leading-loose">
              Aboriginal art is more than visual beauty — it's a living library of stories, laws,
              and maps passed down through countless generations. Each symbol, each pattern
              carries meaning that connects the present to the eternal Dreamtime.
            </p>
            <p className="text-lg text-muted-foreground leading-loose">
              When you acquire a piece from our gallery, you're not just collecting art — you're
              becoming a custodian of ancient wisdom, supporting living artists, and helping
              preserve one of humanity's oldest continuous cultures.
            </p>
          </div>
        </div>
      </section>

      <Testimonials />

      {/* ═══ CLOSING SPREAD ═══ */}
      <ParallaxImage
        src={LIFESTYLE_IMAGES.hotel}
        alt="Arawol artwork in a private residence"
        className="h-[70vh] md:h-[85vh]"
      >
        <div className="absolute inset-0 bg-black/50 flex items-center">
          <div className="w-full px-6 md:px-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <p className="folio text-white/60 mb-6">The Final Word</p>
              <h2 className="text-4xl md:text-6xl font-light text-white leading-tight mb-8">
                {cms("cta_title", "Begin Your Collection")}
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-xl">
                Every acquisition begins with a conversation. Tell us about your space,
                and we will guide you to — or create — the perfect work.
              </p>
              <div className="flex flex-wrap gap-5 items-center">
                <Link to="/the-collection" data-testid="cta-collection-btn">
                  <Button size="lg" className="rounded-full px-10 h-14 text-base">
                    Explore the Collection
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link
                  to="/commissions"
                  className="underline-link text-white text-sm tracking-widest uppercase"
                  data-testid="cta-commissions-link"
                >
                  Commission a Work
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </ParallaxImage>

      <Footer />
    </div>
  );
};

export default HomePage;
